import clsx from 'clsx';

import { Avatar, Chip, Typography } from '@/ui';

import { formatIsoTimeRange } from './calendarGrid';

import type { ScheduleListRow } from '@/features/platformScheduleList/types';
import type { FC } from 'react';

type AppointmentRow = Extract<
  ScheduleListRow,
  { source: 'moments-appointment' | 'platform-admin-appointment' }
>;

export interface ScheduleAppointmentCardProps {
  row: AppointmentRow;
  timeZone?: string | null;
}

const proximityLabel = (
  proximity: NonNullable<AppointmentRow['cancellation']>['proximity'],
): string => {
  if (proximity === 'within_48_hours') return 'Within 48 hours';
  if (proximity === 'outside_48_hours') return 'Outside 48 hours';
  return 'Timing unavailable';
};

const lifecycleLabel = (
  status: AppointmentRow['actionRecord']['view']['lifecycleStatus'],
) =>
  status
    .split('_')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

const ScheduleAppointmentCard: FC<ScheduleAppointmentCardProps> = ({
  row,
  timeZone,
}) => {
  const { view } = row.actionRecord;
  const { cancellation } = row;
  const member = row.displaySession.user;
  const [firstName = '', ...lastNameParts] = member.fullName.split(' ');
  const title =
    view.kind === 'chemistry' ? 'Chemistry call' : 'Coaching session';
  const isCancelled = view.lifecycleStatus === 'cancelled';
  const isCancellationPending = view.lifecycleStatus === 'cancellation_pending';
  const isPending =
    isCancellationPending || view.lifecycleStatus === 'reschedule_pending';
  let lifecycleColor: 'error' | 'success' | 'warning' = 'success';
  if (isCancelled) lifecycleColor = 'error';
  if (isPending) lifecycleColor = 'warning';

  return (
    <div
      className={clsx(
        'flex w-full items-center gap-4 rounded-lg border border-gray-200 px-4 py-3',
        isCancelled ? 'bg-bloom-200/50' : 'bg-white',
      )}
      data-testid="schedule-appointment-card"
    >
      <Avatar
        firstName={firstName}
        lastName={lastNameParts.join(' ')}
        src={member.avatarUrl}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Typography
            className={isCancelled ? 'line-through' : undefined}
            variant="subtitle1"
          >
            {member.fullName}
          </Typography>
          <Typography color="textSecondary" variant="caption">
            {view.coach.displayName}
          </Typography>
        </div>
        <Typography
          className={isCancelled ? 'line-through' : undefined}
          color="textSecondary"
          variant="body2"
        >
          {`${formatIsoTimeRange(
            view.currentSchedule.startAt,
            view.currentSchedule.endAt,
            timeZone,
          )} • ${title}`}
        </Typography>
        {cancellation && (
          <div className="flex gap-2">
            <Typography color="textSecondary" variant="body2">
              {isCancellationPending
                ? `Cancellation pending by ${cancellation.actorLabel}`
                : `Cancelled by ${cancellation.actorLabel}`}
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {proximityLabel(cancellation.proximity)}
            </Typography>
          </div>
        )}
      </div>

      <Chip
        color={lifecycleColor}
        label={lifecycleLabel(view.lifecycleStatus)}
        size="small"
      />
    </div>
  );
};

export default ScheduleAppointmentCard;
